import React, { useEffect, useState } from "react";
import { Plus, Save, Trash2, Edit, X, Gift } from "lucide-react";
import { api } from "../../services/api";
import './AdminUI.css';
import AdminTopbarPortal from './AdminTopbarPortal';

const EMPTY_PROMO = {
  title: "",
  subtitle: "",
  type: "free_item_spend",
  enabled: true,
  minSpend: "",
  rewardItems: [],
  excludedCategoryIds: [],
  excludedMenuItemIds: [],
  bannerImage: "",
  buttonText: "Choose Reward",
  autoApply: true,
  priority: 0,
  startDate: "",
  endDate: ""
};

export default function PromotionsManager() {
  const [promotions, setPromotions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [menuItemSearches, setMenuItemSearches] = useState({});
  const [loading, setLoading] = useState(true);
  const [activePromoIndex, setActivePromoIndex] = useState(null);

  useEffect(() => {
    loadPromotions();
    loadPromotionOptions();
  }, []);

  const loadPromotions = async () => {
    try {
      const data = await api.getAdminPromotions();

      const normalisedPromotions = (data || []).map((promotion) => ({
        ...promotion,
        excludedCategoryIds:
          promotion.excludedCategoryIds || [],
        excludedMenuItemIds:
          promotion.excludedMenuItemIds || []
      }));

      setPromotions(normalisedPromotions);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

const loadPromotionOptions = async () => {
  try {
    const [categoryData, menuItemData] = await Promise.all([
      api.getCategories(),
      api.getMenuItems()
    ]);

      setCategories(categoryData || []);
      setMenuItems(menuItemData || []);
    } catch (err) {
      console.error(
        "Failed to load promotion exclusion options:",
        err
      );
    }
  };

  const updatePromo = (index, field, value) => {
    const updated = [...promotions];
    updated[index][field] = value;
    setPromotions(updated);
  };

  const toggleExcludedValue = (
    promoIndex,
    field,
    value
  ) => {
    const currentValues =
      promotions[promoIndex][field] || [];

    const updatedValues = currentValues.includes(value)
      ? currentValues.filter(
          currentValue => currentValue !== value
        )
      : [...currentValues, value];

    updatePromo(
      promoIndex,
      field,
      updatedValues
    );
  };

  const updateReward = (promoIndex, rewardIndex, field, value) => {
    const updated = [...promotions];

    updated[promoIndex].rewardItems[rewardIndex][field] = value;

    setPromotions(updated);
  };

  const addPromotion = () => {
    setPromotions([
      ...promotions,
      {
        ...EMPTY_PROMO,
        id: `temp-${Date.now()}`
      }
    ]);
    setActivePromoIndex(promotions.length);
  };

  const closePromotionEditor = () => {
    if (activePromoIndex !== null && promotions[activePromoIndex]?.id?.startsWith("temp-")) {
      setPromotions(current => current.filter((_, index) => index !== activePromoIndex));
    }
    setActivePromoIndex(null);
  };

  const addRewardItem = (promoIndex) => {
    const updated = [...promotions];

    updated[promoIndex].rewardItems.push({
      id: crypto.randomUUID(),
      name: "",
      description: "",
      image: "",
      price: 0
    });

    setPromotions(updated);
  };

  const savePromotion = async (promo) => {
    try {
      const payload = { ...promo, minSpend: Number(promo.minSpend || 0) };

      if (promo.id.startsWith("temp-")) {
        await api.createPromotion(payload);
      } else {
        await api.updatePromotion(promo.id, payload);
      }

      await loadPromotions();
      setActivePromoIndex(null);

    } catch (err) {
      console.error(err);
      alert("Failed to save promotion");
    }
  };

  const deletePromotion = async (id) => {
    if (!window.confirm("Delete promotion?")) {
      return;
    }

    try {
      if (id.startsWith("temp-")) {
        setPromotions(current => current.filter(promotion => promotion.id !== id));
        setActivePromoIndex(null);
        return;
      }
      await api.deletePromotion(id);
      await loadPromotions();
      setActivePromoIndex(null);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        Loading promotions...
      </div>
    );
  }

  return (
    <div className="admin-view space-y-6">

      <AdminTopbarPortal><div className="admin-topbar-controls">
        <h2 className="text-xl font-bold">
          Promotions
        </h2>

        <button
          onClick={addPromotion}
          className="bg-black text-white px-4 py-2 rounded-xl flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Promotion
        </button>
      </div></AdminTopbarPortal>

      {promotions.length === 0 ? (
        <div className="admin-empty-state"><Gift className="w-8 h-8" />No promotions yet. Add one to get started.</div>
      ) : (
        <div className="admin-promotion-list">
          {promotions.map((promo, index) => (
            <div key={promo.id} className="admin-promotion-card">
              <div className="admin-promotion-icon"><Gift /></div>
              <div className="admin-promotion-copy">
                <div><strong>{promo.title || 'Untitled promotion'}</strong><span className={promo.enabled ? 'is-enabled' : ''}>{promo.enabled ? 'Active' : 'Inactive'}</span></div>
                <p>{promo.subtitle || 'No customer-facing description yet.'}</p>
                <small>Minimum spend £{Number(promo.minSpend || 0).toFixed(2)} · {promo.rewardItems?.length || 0} reward items</small>
              </div>
              <button onClick={() => setActivePromoIndex(index)} className="admin-secondary-button"><Edit className="w-4 h-4" /> Edit</button>
            </div>
          ))}
        </div>
      )}

      {promotions.map((promo, index) => activePromoIndex === index && (
        <div className="admin-drawer-backdrop" key={promo.id} onMouseDown={closePromotionEditor}>
        <div
          className="admin-drawer admin-drawer-wide space-y-4"
          onMouseDown={event => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Edit promotion"
        >

          <div className="admin-drawer-heading">
            <div><span>Promotion</span><h3>{promo.id.startsWith('temp-') ? 'New promotion' : 'Edit promotion'}</h3></div>
            <button onClick={closePromotionEditor} aria-label="Close promotion editor"><X /></button>
          </div>

          <input
            value={promo.title}
            onChange={(e) =>
              updatePromo(index, "title", e.target.value)
            }
            placeholder="Promotion title"
            className="w-full border border-gray-200 rounded-xl px-4 py-3"
          />

          <input
            value={promo.subtitle}
            onChange={(e) =>
              updatePromo(index, "subtitle", e.target.value)
            }
            placeholder="Promotion subtitle"
            className="w-full border border-gray-200 rounded-xl px-4 py-3"
          />

          <label className="admin-promotion-spend-field">
            <span>Minimum spend requirement (£)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={promo.minSpend}
              onChange={(e) => updatePromo(index, "minSpend", e.target.value === "" ? "" : Number(e.target.value))}
              placeholder="Minimum spend requirement"
              className="w-full border border-gray-200 rounded-xl px-4 py-3"
            />
          </label>

          <div className="border border-gray-200 rounded-xl p-4">
            <div className="mb-3">
              <h3 className="font-semibold text-gray-900">
                Exclude Categories
              </h3>

              <p className="text-sm text-gray-500 mt-1">
                Spend on items within selected categories will not
                count towards this promotion.
              </p>
            </div>

            {categories.length === 0 ? (
              <p className="text-sm text-gray-400">
                No categories available.
              </p>
            ) : (
              <div className="admin-promotion-picker-grid">
                {categories.map(category => {
                  const isExcluded = (
                    promo.excludedCategoryIds || []
                  ).includes(category.id);

                  return (
                    <label
                      key={category.id}
                      className={`
                        flex
                        items-center
                        gap-3
                        border
                        rounded-xl
                        px-3
                        py-3
                        cursor-pointer
                        transition-colors
                        ${
                          isExcluded
                            ? "border-red-300 bg-red-50"
                            : "border-gray-200 bg-white"
                        }
                      `}
                    >
                      <input
                        type="checkbox"
                        checked={isExcluded}
                        onChange={() =>
                          toggleExcludedValue(
                            index,
                            "excludedCategoryIds",
                            category.id
                          )
                        }
                        className="w-4 h-4"
                      />

                      <span className="text-sm font-medium text-gray-800">
                        {category.name}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border border-gray-200 rounded-xl p-4">
            <div className="mb-3">
              <h3 className="font-semibold text-gray-900">
                Exclude Individual Menu Items
              </h3>

              <p className="text-sm text-gray-500 mt-1">
                Spend on selected menu items will not count towards
                this promotion.
              </p>
            </div>

            <input
              type="text"
              value={menuItemSearches[promo.id] || ""}
              onChange={(e) =>
                setMenuItemSearches(current => ({
                  ...current,
                  [promo.id]: e.target.value
                }))
              }
              placeholder="Search menu items..."
              className="w-full border border-gray-200 rounded-xl px-4 py-3 mb-3"
            />

            {menuItems.length === 0 ? (
              <p className="text-sm text-gray-400">
                No menu items available.
              </p>
            ) : (
              <div className="admin-promotion-item-picker">
                {menuItems
                  .filter(item => {
                    const searchValue = (
                      menuItemSearches[promo.id] || ""
                    )
                      .trim()
                      .toLowerCase();

                    if (!searchValue) {
                      return true;
                    }

                    const category = categories.find(
                      category =>
                        category.id === item.categoryId
                    );

                    return (
                      item.name
                        ?.toLowerCase()
                        .includes(searchValue) ||
                      category?.name
                        ?.toLowerCase()
                        .includes(searchValue)
                    );
                  })
                  .map(item => {
                    const category = categories.find(
                      category =>
                        category.id === item.categoryId
                    );

                    const isIndividuallyExcluded = (
                      promo.excludedMenuItemIds || []
                    ).includes(item.id);

                    const isCategoryExcluded = (
                      promo.excludedCategoryIds || []
                    ).includes(item.categoryId);

                    return (
                      <label
                        key={item.id}
                        className={`
                          flex
                          items-center
                          justify-between
                          gap-3
                          border
                          rounded-xl
                          px-3
                          py-3
                          transition-colors
                          ${
                            isIndividuallyExcluded
                              ? "border-red-300 bg-red-50 cursor-pointer"
                              : isCategoryExcluded
                                ? "border-gray-200 bg-gray-100 opacity-70 cursor-not-allowed"
                                : "border-gray-200 bg-white cursor-pointer"
                          }
                        `}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <input
                            type="checkbox"
                            checked={isIndividuallyExcluded}
                            disabled={isCategoryExcluded}
                            onChange={() =>
                              toggleExcludedValue(
                                index,
                                "excludedMenuItemIds",
                                item.id
                              )
                            }
                            className="w-4 h-4 flex-shrink-0"
                          />

                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {item.name}
                            </p>

                            <p className="text-xs text-gray-500 truncate">
                              {category?.name || "Uncategorised"}
                            </p>
                          </div>
                        </div>

                        {isCategoryExcluded && (
                          <span className="text-xs font-medium text-gray-500 flex-shrink-0">
                            Category excluded
                          </span>
                        )}
                      </label>
                    );
                  })}
              </div>
            )}
          </div>

          <input
            value={promo.bannerImage}
            onChange={(e) =>
              updatePromo(index, "bannerImage", e.target.value)
            }
            placeholder="Banner image URL"
            className="w-full border border-gray-200 rounded-xl px-4 py-3"
          />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">
                Reward Items
              </h3>

              <button
                onClick={() => addRewardItem(index)}
                className="text-sm font-semibold text-amber-700"
              >
                + Add Reward Item
              </button>
            </div>

            {promo.rewardItems?.map((reward, rewardIndex) => (
              <div
                key={reward.id}
                className="admin-reward-item border border-gray-200 rounded-xl p-3"
              >

                <input
                  value={reward.name}
                  onChange={(e) =>
                    updateReward(
                      index,
                      rewardIndex,
                      "name",
                      e.target.value
                    )
                  }
                  placeholder="Reward item name"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3"
                />

                <input
                  value={reward.description}
                  onChange={(e) =>
                    updateReward(
                      index,
                      rewardIndex,
                      "description",
                      e.target.value
                    )
                  }
                  placeholder="Reward description"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3"
                />

                <input
                  value={reward.image}
                  onChange={(e) =>
                    updateReward(
                      index,
                      rewardIndex,
                      "image",
                      e.target.value
                    )
                  }
                  placeholder="Reward image URL"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3"
                />

              </div>
            ))}
          </div>

          <div className="admin-drawer-footer">

            <button
              onClick={() => savePromotion(promo)}
              className="bg-gray-900 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 font-semibold"
            >
              <Save className="w-4 h-4" />
              Save
            </button>

            <button onClick={closePromotionEditor} className="px-5 py-2.5 text-gray-600 rounded-xl font-semibold hover:bg-gray-100">Cancel</button>

            <button
              onClick={() => deletePromotion(promo.id)}
              className="admin-drawer-delete bg-red-100 text-red-700 px-4 py-2.5 rounded-xl flex items-center gap-2 font-semibold"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>

          </div>

        </div>
        </div>
      ))}

    </div>
  );
}
